import * as health from "../../lib/api/health";
import * as posActions from "../../lib/posActions";
import * as posModels from "../../lib/api/posModels";
import * as routeHelpers from "../../lib/routeHelpers";
import cn from "classnames";
import describeNetworkError from "../../lib/describeNetworkError";
import ErrorModal from "../ErrorModal";
import HealthCheckModal from "../HealthCheckModal";
import logger from "../../lib/sentryLogger";
import ManagerHeader from "../ManagerHeader";
import OutlineButton from "../OutlineButton";
import React, {useEffect, useRef, useState} from "react";
import styles from "./ManagerSettings.module.css";
import ToggleButton from "../ToggleButton";
import {useAppSelector, useAppDispatch} from "../../lib/hooks";
import {useNavigate} from "react-router-dom";
import TextInput from "../TextInput";
import StyledKeyboard from "../StyledKeyboard";
import {Actions, API} from "habit-core";

const strings = {
    title: "Settings",
    stationMode: "Station Mode",
    laneAssignment: "Lane Assignment",
    save: "Save",
    healthCheckSectionTitle: "Health Check",
    runHealthCheck: "Run Health Check",
    willRequireTill:
        "To change the station mode, you will be required to assign a till.",
    enableCCPayment: "Enable CC Payment",
    stationModeChangeError:
        "An unexpected error occurred when recording the station mode change. Please try again.",
    kioskCashHeader: "Kiosk Cash Payment Pager Number",
    enterNumber: "Enter Number",
    autoPrintReceipts: "Automatically Print Receipts",
    yes: "Yes",
    no: "No",
    errorMessage: "Failed to save the following settings:",
    till: "Till",
    outOfPennies: "Out of Pennies",
    storeSettings: "Store Settings",
};

const MODES: {label: posModels.StationMode; value: string}[] = [
    {label: "front_of_house_1", value: "Front of House 1"},
    {label: "front_of_house_2", value: "Front of House 2"},
    {label: "line_buster", value: "Line Buster"},
    {label: "drive_thru_order_taker", value: "Drive Thru Order Taker"},
    {
        label: "drive_thru_order_fulfillment",
        value: "Drive Thru Order Fulfillment",
    },
];

const LANE_OPTIONS: {value: posModels.LaneAssignment; label: string}[] = [
    {value: "lane_1", label: "Lane 1"},
    {value: "lane_2", label: "Lane 2"},
    {value: "both", label: "Both"},
];

const PENNIES_UNAVAILABLE_OPTIONS: {value: boolean; label: string}[] = [
    {label: "No", value: false},
    {label: "Yes", value: true},
];

export default function ManagerSettings() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const tillAssignmentSkipped = useAppSelector(
        (state) => state.pos.station.tillAssignmentSkipped,
    );
    const currentStationMode = useAppSelector(
        (state) => state.pos.station.mode,
    );
    const currentLaneAssignment = useAppSelector(
        (state) => state.pos.station.laneAssignment,
    );
    const initialAutoPrintReceipts = useAppSelector(
        (state) => state.pos.station.autoPrintReceipts,
    );
    const [autoPrintReceipts, setAutoPrintReceipts] = useState(
        initialAutoPrintReceipts,
    );
    const initialPenniesUnavailable = useAppSelector(
        (state) => state.pos.store.penniesUnavailable,
    );
    const initialKioskPagerNumber = useAppSelector(
        (state) => state.pos.store.kioskCashOrderPagerNumber,
    );
    const [kioskPagerNumber, setKioskPagerNumber] = useState(
        initialKioskPagerNumber?.toString() ?? "",
    );
    const isCCPaymentDisabled = useAppSelector(
        (state) => state.pos.station.isCCPaymentDisabled,
    );
    const [selectedStationMode, setSelectedStationMode] =
        useState(currentStationMode);
    const [selectedLaneAssignment, setSelectedLaneAssignment] =
        useState<posModels.LaneAssignment>(currentLaneAssignment ?? "lane_1");
    const [penniesUnavailable, setPenniesUnavailable] = useState(
        initialPenniesUnavailable,
    );
    const [isStationModeUpdated, setIsStationModeUpdated] = useState(false);

    const currentOrder = useAppSelector((state) => state.pos.currentOrder);
    const isCurrentOrderPending = useAppSelector(
        (state) => !!state.pos.pendingOrders.byId[currentOrder.id ?? ""],
    );

    const [healthCheckData, setHealthCheckData] = useState<
        posModels.ExhaustiveHealthCheckResponse | undefined
    >(undefined);
    const [healthCheckLoading, setHealthCheckLoading] = useState(false);
    const [healthCheckError, setHealthCheckError] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [saving, setSaving] = useState(false);

    const runHealthCheck = () => {
        setHealthCheckLoading(true);
        health
            .getExhaustiveHealth()
            .then((data) => {
                setHealthCheckData(data);
            })
            .catch((err) => {
                logger.error("Failed to make exhaustive health check.");
                setHealthCheckError(describeNetworkError(err).join("\n"));
            })
            .finally(() => {
                setHealthCheckLoading(false);
            });
    };
    const header = (
        <ManagerHeader
            label={strings.title}
            buttonLabel={strings.runHealthCheck}
            buttonOnClick={runHealthCheck}
            buttonLoading={healthCheckLoading}
        />
    );

    useEffect(() => {
        if (
            selectedStationMode !== currentStationMode ||
            selectedLaneAssignment !== currentLaneAssignment
        ) {
            setIsStationModeUpdated(true);
        } else {
            setIsStationModeUpdated(false);
        }
    }, [
        selectedStationMode,
        currentStationMode,
        selectedLaneAssignment,
        currentLaneAssignment,
    ]);

    /* keyboard state */
    const [keyboardCollapsed, setKeyboardCollapsed] = useState(true);
    const kioskPagerNumberInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (kioskPagerNumberInputRef.current && !keyboardCollapsed) {
            if (document.activeElement === kioskPagerNumberInputRef.current) {
                kioskPagerNumberInputRef?.current?.scrollIntoView();
            }
        }
    }, [keyboardCollapsed]);

    const onChangeKioskPagerNumber = (input: string) => {
        setKioskPagerNumber(input);
    };

    function enableCCPayment() {
        dispatch(posActions.setStationCCPaymentDisabled(false));
        dispatch(posActions.setStationCashier({type: "user"}));
        navigate(routeHelpers.deviceSetup());
    }

    function onSave() {
        setSaving(true);

        const hasKioskPagerChanges =
            kioskPagerNumber !== initialKioskPagerNumber?.toString();
        const hasAutoPrintChanges =
            initialAutoPrintReceipts !== autoPrintReceipts;
        const hasPenniesUnavailableChanges =
            initialPenniesUnavailable !== penniesUnavailable;
        const autoPrintPromise = hasAutoPrintChanges
            ? dispatch(posActions.editAutoPrintReceipts(autoPrintReceipts))
            : Promise.resolve();
        const updateStoreSettingsPromise =
            hasKioskPagerChanges || hasPenniesUnavailableChanges
                ? dispatch(
                      posActions.updateStoreSettings(
                          penniesUnavailable,
                          isNaN(parseInt(kioskPagerNumber))
                              ? null
                              : parseInt(kioskPagerNumber),
                      ),
                  )
                : Promise.resolve();
        const finalLaneAssignment =
            selectedStationMode === "drive_thru_order_taker" ||
            selectedStationMode === "line_buster"
                ? selectedLaneAssignment
                : null;
        const stationModePromise = isStationModeUpdated
            ? dispatch(
                  posActions.editStationMode(
                      selectedStationMode,
                      finalLaneAssignment,
                  ),
              )
            : Promise.resolve();

        Promise.allSettled([
            autoPrintPromise,
            updateStoreSettingsPromise,
            stationModePromise,
        ])
            .then(([autoPrintRes, updateStoreSettingsRes, stationModeRes]) => {
                if (
                    autoPrintRes.status === "rejected" ||
                    updateStoreSettingsRes.status === "rejected" ||
                    stationModeRes.status === "rejected"
                ) {
                    let errorMessages = "";
                    if (autoPrintRes.status === "rejected") {
                        logger.error("Failed to save auto print receipts", {
                            errorMessage: autoPrintRes.reason,
                        });
                        errorMessages = errorMessages.concat(
                            "\n",
                            strings.autoPrintReceipts,
                            `: ${autoPrintRes.reason}`,
                        );
                    }
                    if (updateStoreSettingsRes.status === "rejected") {
                        logger.error("Failed to update store settings", {
                            errorMessage: updateStoreSettingsRes.reason,
                        });
                        errorMessages = errorMessages.concat(
                            "\n",
                            strings.storeSettings,
                            `: ${updateStoreSettingsRes.reason}`,
                        );
                    }
                    if (stationModeRes.status === "rejected") {
                        logger.error("Failed to edit station mode", {
                            errorMessage: stationModeRes.reason,
                        });
                        errorMessages = errorMessages.concat(
                            "\n",
                            strings.stationMode,
                            `: ${stationModeRes.reason}`,
                        );
                    }
                    setErrorMessage(`${strings.errorMessage}${errorMessages}`);
                }

                if (stationModeRes.status === "fulfilled") {
                    if (
                        (currentStationMode === "drive_thru_order_taker" ||
                            currentStationMode === "line_buster") &&
                        selectedStationMode !== "drive_thru_order_taker" &&
                        selectedStationMode !== "line_buster"
                    ) {
                        dispatch(posActions.setTillAssignmentSkipped(false));
                    }

                    const newOrderType: API.models.OrderType =
                        selectedStationMode === "front_of_house_1" ||
                        selectedStationMode === "front_of_house_2"
                            ? "in_store"
                            : "drive_thru";

                    let newOrderTypeSet = false;

                    if (
                        selectedStationMode !== "drive_thru_order_fulfillment"
                    ) {
                        if (isCurrentOrderPending) {
                            newOrderTypeSet = true;
                            dispatch(posActions.startNewOrder(newOrderType));
                        } else if (!currentOrder.id) {
                            dispatch(posActions.reserveCurrentOrderId());
                        }
                    } else {
                        dispatch(posActions.clearCurrentOrder());
                    }

                    if (!newOrderTypeSet) {
                        dispatch(
                            Actions.currentOrderActions.selectOrderType(
                                newOrderType,
                            ),
                        );
                    }
                }

                if (
                    autoPrintRes.status === "fulfilled" &&
                    updateStoreSettingsRes.status === "fulfilled" &&
                    stationModeRes.status === "fulfilled"
                ) {
                    navigate(routeHelpers.deviceSetup(), {
                        state: {
                            skipCCRegistrationCheck:
                                selectedStationMode ===
                                    "drive_thru_order_taker" ||
                                selectedStationMode === "line_buster",
                        },
                    });
                    dispatch(posActions.setStationCashier({type: "user"}));
                }
            })
            .finally(() => {
                setSaving(false);
            });
    }

    return (
        <>
            <div className={styles.container}>
                {header}
                <div className={styles.content}>
                    <div className={styles.section}>
                        <div className={styles.text}>{strings.stationMode}</div>
                        <div className={styles.modes}>
                            {MODES.map((m, i) => (
                                <ToggleButton
                                    key={i}
                                    className={styles.mode}
                                    label={m.value}
                                    checked={selectedStationMode === m.label}
                                    onChange={() =>
                                        setSelectedStationMode(m.label)
                                    }
                                />
                            ))}
                        </div>
                        {(currentStationMode === "drive_thru_order_taker" ||
                            currentStationMode === "line_buster") &&
                        tillAssignmentSkipped ? (
                            <div className={styles.text}>
                                {strings.willRequireTill}
                            </div>
                        ) : null}
                        {isCCPaymentDisabled ? (
                            <OutlineButton
                                label={strings.enableCCPayment}
                                onClick={enableCCPayment}
                            />
                        ) : null}
                        <div>
                            <div className={styles.text}>
                                {strings.kioskCashHeader}
                            </div>
                            <TextInput
                                disabled={saving}
                                value={kioskPagerNumber}
                                onChange={(e) => {
                                    const str = e.target.value;
                                    const val = str.replace(/[^\d]/g, "");
                                    onChangeKioskPagerNumber(val);
                                }}
                                placeholder={strings.enterNumber}
                                onFocus={() => setKeyboardCollapsed(false)}
                                ref={kioskPagerNumberInputRef}
                                inputMode="none"
                            />
                        </div>
                        <div className={styles.autoPrintReceiptsSection}>
                            <div className={styles.text}>
                                {strings.autoPrintReceipts}
                            </div>
                            <div className={styles.autoPrintOptions}>
                                <ToggleButton
                                    className={styles.autoPrintButton}
                                    label={strings.yes}
                                    checked={autoPrintReceipts}
                                    onChange={() => setAutoPrintReceipts(true)}
                                    disabled={saving}
                                />
                                <ToggleButton
                                    className={styles.autoPrintButton}
                                    label={strings.no}
                                    checked={!autoPrintReceipts}
                                    onChange={() => setAutoPrintReceipts(false)}
                                    disabled={saving}
                                />
                            </div>
                        </div>
                    </div>
                    {selectedStationMode === "drive_thru_order_taker" ||
                    selectedStationMode === "line_buster" ? (
                        <div className={styles.section}>
                            <div className={styles.text}>
                                {strings.laneAssignment}
                            </div>
                            <div className={styles.laneAssignments}>
                                {LANE_OPTIONS.map((l) => (
                                    <ToggleButton
                                        key={l.value}
                                        className={styles.laneAssignment}
                                        label={l.label}
                                        checked={
                                            selectedLaneAssignment === l.value
                                        }
                                        onChange={() =>
                                            setSelectedLaneAssignment(l.value)
                                        }
                                    />
                                ))}
                            </div>
                        </div>
                    ) : null}
                    <div className={styles.section}>
                        <div className={styles.text}>{strings.till}</div>
                        <div
                            className={cn(
                                styles.text,
                                styles.penniesUnavailableHeader,
                            )}>
                            {strings.outOfPennies}
                        </div>
                        <div className={styles.penniesUnavailableOptions}>
                            {PENNIES_UNAVAILABLE_OPTIONS.map((o, i) => (
                                <ToggleButton
                                    key={i}
                                    className={styles.mode}
                                    label={o.label}
                                    checked={penniesUnavailable === o.value}
                                    onChange={() =>
                                        setPenniesUnavailable(o.value)
                                    }
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <div className={styles.footer}>
                    <OutlineButton
                        className={styles.footerButton}
                        label={strings.save}
                        disabled={
                            !isStationModeUpdated &&
                            kioskPagerNumber ===
                                initialKioskPagerNumber?.toString()
                        }
                        onClick={onSave}
                        loading={saving}
                    />
                </div>
                <div
                    className={cn(
                        styles.keyboardContainer,
                        keyboardCollapsed && styles.keyboardContainerCollapsed,
                    )}>
                    <StyledKeyboard
                        currentInput={kioskPagerNumber}
                        visible={!keyboardCollapsed}
                        setVisible={(val: boolean) =>
                            setKeyboardCollapsed(!val)
                        }
                        onChange={onChangeKioskPagerNumber}
                        inputRefs={[kioskPagerNumberInputRef]}
                        layout="numeric"
                        onPressEnter={onSave}
                    />
                </div>
            </div>
            {healthCheckData ? (
                <HealthCheckModal
                    healthCheckData={healthCheckData}
                    onClose={() => setHealthCheckData(undefined)}
                />
            ) : null}
            {healthCheckError ? (
                <ErrorModal
                    title="Health Check Error"
                    errorMessage={healthCheckError}
                    showITInfo
                    onClose={() => setHealthCheckError("")}
                />
            ) : null}
            {errorMessage ? (
                <ErrorModal
                    errorMessage={errorMessage}
                    showITInfo
                    onClose={() => setErrorMessage("")}
                />
            ) : null}
        </>
    );
}
